import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Row,
  Typography,
  Spin,
  Col,
  Modal,
  Input,
  Button,
  Tooltip,
  Dropdown,
  Pagination,
  message,
} from 'antd'
import { useTranslation } from 'react-i18next'
import styles from '../Organizations.module.less'
import { WaitOverlay } from '~/Components'
import { GroupsTable } from './GroupsTable'
import { CreateUpdateGroupForm } from './CreateUpdateGroupForm'
import {
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '~/Store/hooks'
import { OrganizationActions, OrgModelName } from '~/Store/Organization'
import { getModalVisibility } from '~/Store/Organization/selectors'
import { GroupActions, selectGroupState } from '~/Store/Groups'
import { getDefaultOrganization } from '~/Store/User/selectors'
import { GroupInfo } from '~/Services/GroupServices'
import { Utils } from '~/Utils'
import type { MenuProps } from 'antd'
import { getLeftSiderCollapsed } from '~/Store/Layout/selectors'
import { useAntdBackendTable } from '~/Hooks/useAntdBackendTable'
import { UploadFileModal } from '~/Components/Upload/UploadfileModal'

export const Groups = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const leftMenuCollapsed = useAppSelector(getLeftSiderCollapsed)
  const [selectedGroup, setSelectedGroup] = useState<GroupInfo | null>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [hasInitialized, setHasInitialized] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const currentFiltersRef = useRef<any>({})
  const currentSortRef = useRef<any>(undefined)

  const defaultOrg = useAppSelector(getDefaultOrganization)
  const {
    groupData,
    loading,
    page,
    pageSize,
    totalCount,
    keyword,
    columnFilters,
    sort,
  } = useAppSelector(selectGroupState)

  const isModalVisible = useAppSelector(
    getModalVisibility(OrgModelName.createUpdateJobTitleModal),
  )

  // Build keyword query
  const buildKeywordQuery = (keyword: string) => {
    if (!keyword.trim()) return ''

    return `(contains(code,'${keyword}') or contains(name,'${keyword}') or contains(note,'${keyword}'))`
  }

  const buildFinalFilter = (keyword: string, columnFilterQuery?: string) => {
    const keywordQuery = buildKeywordQuery(keyword)

    if (keywordQuery && columnFilterQuery)
      return `${keywordQuery} and ${columnFilterQuery}`

    return keywordQuery || columnFilterQuery || ''
  }

  // Fetch data
  const fetchGroups = useCallback(
    ({
      pageParam = page,
      pageSizeParam = pageSize,
      columnFilterQuery,
      columnFiltersData,
      sort,
    }: {
      pageParam?: number
      pageSizeParam?: number
      columnFilterQuery?: string
      columnFiltersData?: any
      sort?: any
    }) => {
      if (!defaultOrg?.guid) return

      const finalFilter = buildFinalFilter(keyword, columnFilterQuery)

      dispatch(
        GroupActions.getGroupList({
          orgId: defaultOrg.guid,
          page: pageParam,
          pageSize: pageSizeParam,
          filter: {
            queryString: finalFilter,
            columnFilters: columnFiltersData || {},
          },
          sort,
          expand: 'Licenses',
        }),
      )
    },
    [defaultOrg?.guid, keyword, dispatch],
  )

  // Initial
  useEffect(() => {
    if (defaultOrg?.guid && !hasInitialized) {
      const columnFilterQuery = Utils.buildAntdFilterQuery(columnFilters)
      fetchGroups({
        pageParam: page,
        pageSizeParam: pageSize,
        columnFilterQuery,
        columnFiltersData: columnFilters,
        sort,
      })
      setHasInitialized(true)
    }
  }, [
    defaultOrg?.guid,
    hasInitialized,
    fetchGroups,
    page,
    pageSize,
    columnFilters,
    sort,
  ])

  // Table change
  const fetchGroupsData = useCallback(
    (params: any) => {
      fetchGroups({
        pageParam: params.page,
        pageSizeParam: params.pageSize,
        columnFilterQuery: params.filter,
        sort: params.sort,
      })
    },
    [fetchGroups],
  )

  const { handleTableChange: handleAntdTableChange } =
    useAntdBackendTable(fetchGroupsData)
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    currentFiltersRef.current = filters
    dispatch(GroupActions.setColumnFilters(filters))
    
    if (sorter?.field && sorter?.order) {
      const sortData = { field: sorter.field, order: sorter.order }
      currentSortRef.current = sortData
      dispatch(GroupActions.setSort(sortData))
    } else if (sorter?.field === undefined) {
      currentSortRef.current = undefined
      dispatch(GroupActions.setSort(undefined))
    }

    const paginationData = {
      current: page,
      pageSize: pageSize,
    }
    handleAntdTableChange(paginationData, filters, sorter)
  }

  // Reset filters khi đổi organization hoặc unmount
  useEffect(() => {
    return () => {
      dispatch(GroupActions.resetFilter())
    }
  }, [dispatch, defaultOrg?.guid])

  // Search with debounce
  useEffect(() => {
    if (!defaultOrg?.guid || !hasInitialized) return

    const timer = setTimeout(() => {
      const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
      fetchGroups({
        pageParam: 1,
        pageSizeParam: pageSize,
        columnFilterQuery,
        columnFiltersData: currentFiltersRef.current,
        sort: currentSortRef.current, 
      })
    }, 500)

    return () => clearTimeout(timer)
  }, [
    keyword,
    defaultOrg?.guid,
    fetchGroups,
    pageSize,
  ])

  // Pagination
  const handlePageChange = (page: number, pageSize?: number) => {
    const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
    fetchGroups({
      pageParam: page,
      pageSizeParam: pageSize,
      columnFilterQuery,
      columnFiltersData: currentFiltersRef.current,
      sort: currentSortRef.current,
    })
  }

  const showCreateUpdateJobTitleModal = () => {
    setSelectedGroup(null)
    dispatch(
      OrganizationActions.setModalState({
        name: OrgModelName.createUpdateJobTitleModal,
        value: true,
      }),
    )
  }

  const handleEdit = (group: GroupInfo) => {
    setSelectedGroup(group)
    dispatch(
      OrganizationActions.setModalState({
        name: OrgModelName.createUpdateJobTitleModal,
        value: true,
      }),
    )
  }

  const handleCancel = () => {
    setSelectedGroup(null)
    dispatch(
      OrganizationActions.setModalState({
        name: OrgModelName.createUpdateJobTitleModal,
        value: false,
      }),
    )
  }

  const handleSubmit = (values: any) => {
    if (!defaultOrg?.guid) return

    const groupData: GroupInfo = {
      code: values.code,
      name: values.displayName,
      status: values.status ? 1 : 0,
      note: values.note || '',
      organizationId: defaultOrg.guid,
    }

    if (selectedGroup?.id) {
      // Update
      dispatch(
        GroupActions.updateGroup({
          groupId: selectedGroup.id,
          data: groupData,
          onSuccess: () => {
            handleCancel()
            const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
            fetchGroups({
              pageParam: page,
              pageSizeParam: pageSize,
              columnFilterQuery,
              columnFiltersData: currentFiltersRef.current,
              sort: currentSortRef.current,
            })
          },
        }),
      )
    } else {
      // Create
      dispatch(
        GroupActions.createGroup({
          data: groupData,
          onSuccess: () => {
            handleCancel()
            const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
            fetchGroups({
              pageParam: 1,
              pageSizeParam: pageSize,
              columnFilterQuery,
              columnFiltersData: currentFiltersRef.current,
              sort: currentSortRef.current,
            })
          },
        }),
      )
    }
  }

  const handleDelete = (groupId: string) => {
    if (!defaultOrg?.guid) return

    Modal.confirm({
      title: t('groups.confirmDeleteGroup', { groupName: '' }),
      okText: t('remove'),
      okType: 'danger',
      cancelText: t('cancel'),
      onOk: () => {
        dispatch(
          GroupActions.deleteGroup({
            orgId: defaultOrg.guid,
            groupIds: [groupId],
            onSuccess: () => {
              const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
              fetchGroups({
                pageParam: page,
                pageSizeParam: pageSize,
                columnFilterQuery,
                columnFiltersData: currentFiltersRef.current,
                sort: currentSortRef.current,
              })
            },
          }),
        )
      },
    })
  }

  const handleDeleteMultiple = (groupIds: string[]) => {
    if (!defaultOrg?.guid) return

    Modal.confirm({
      title: '',
      content: t('groups.confirmDeleteGroup', {
        count: groupIds.length,
        ns: 'organizations',
      }),
      okText: t('remove'),
      okType: 'danger',
      cancelText: t('cancel'),
      onOk: () => {
        dispatch(
          GroupActions.deleteGroup({
            orgId: defaultOrg.guid,
            groupIds,
            onSuccess: () => {
              const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
              fetchGroups({
                pageParam: page,
                pageSizeParam: pageSize,
                columnFilterQuery,
                columnFiltersData: currentFiltersRef.current,
                sort: currentSortRef.current,
              })
              setSelectedRowKeys([])
            },
          }),
        )
      },
    })
  }

  const handleSearch = () => {
    if (!defaultOrg?.guid) return
    const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
    fetchGroups({
      pageParam: 1,
      pageSizeParam: pageSize,
      columnFilterQuery,
      columnFiltersData: currentFiltersRef.current,
      sort: currentSortRef.current,
    })
  }

  const handleExportAll = () => {
    if (!defaultOrg?.guid) return

    dispatch(
      GroupActions.exportAllGroups({
        orgId: defaultOrg.guid,
        onSuccess: data => {
          Utils.downloadFile(data, `groups-all-${new Date().getTime()}.xlsx`)
          message.success(t('exportSuccess'))
        },
        onFailure: error => {
          message.error(error)
        },
      }),
    )
  }

  const handleExportCurrentPage = () => {
    if (!defaultOrg?.guid) return

    const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
    const finalFilter = buildFinalFilter(keyword, columnFilterQuery)

    dispatch(
      GroupActions.exportGroupsInCurrentPage({
        orgId: defaultOrg.guid,
        page,
        pageSize,
        filter: {
          queryString: finalFilter,
          columnFilters: currentFiltersRef.current,
        },
        sort: currentSortRef.current,
        expand: 'Licenses',
        onSuccess: data => {
          Utils.downloadFile(
            data,
            `groups-page-${page}-${new Date().getTime()}.xlsx`,
          )
          message.success(t('exportSuccess'))
        },
        onFailure: error => {
          message.error(error)
        },
      }),
    )
  }

  const handleExportSelected = () => {
    if (!defaultOrg?.guid || selectedRowKeys.length === 0) return

    dispatch(
      GroupActions.exportSelectedGroups({
        orgId: defaultOrg.guid,
        groupIds: selectedRowKeys,
        onSuccess: data => {
          Utils.downloadFile(
            data,
            `groups-selected-${selectedRowKeys.length}-${new Date().getTime()}.xlsx`,
          )
          message.success(t('exportSuccess'))
        },
        onFailure: error => {
          message.error(error)
        },
      }),
    )
  }

  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true)
  }

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false)
  }

  const uploadGroupActionCreator = (file: File) => {
    if (!defaultOrg?.guid) return
    return GroupActions.importGroups({
      orgId: defaultOrg.guid,
      file,
      onSuccess: () => {
        message.success(t('uploadSuccess'))
        const columnFilterQuery = Utils.buildAntdFilterQuery(currentFiltersRef.current)
        fetchGroups({
          pageParam: 1,
          pageSizeParam: pageSize,
          columnFilterQuery,
          columnFiltersData: currentFiltersRef.current,
          sort: currentSortRef.current,
        })
      },
      onFailure: () => {
        message.error(t('uploadFail'))
      },
    })
  }

  const downloadMenuItems: MenuProps['items'] = [
    {
      key: 'all',
      label: t('exportAll'),
      onClick: handleExportAll,
    },
    {
      key: 'current',
      label: t('exportCurrentPage'),
      onClick: handleExportCurrentPage,
    },
    {
      key: 'selected',
      label: t('exportSelected'),
      onClick: handleExportSelected,
      disabled: selectedRowKeys.length === 0,
    },
  ]

  return (
    <WaitOverlay name={''} size="large" wrapperClassName={styles.FullOverlay}>
      <Modal
        className={styles.CustomModal}
        title={
          selectedGroup ? t('groups.updateGroup') : t('groups.createGroup')
        }
        visible={isModalVisible}
        width={700}
        okButtonProps={{ disabled: true, hidden: true }}
        cancelButtonProps={{ disabled: true, hidden: true }}
        onCancel={handleCancel}
        footer={null}
      >
        <CreateUpdateGroupForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          initialData={selectedGroup}
        />
      </Modal>

      <UploadFileModal
        open={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        actionCreator={uploadGroupActionCreator}
      />

      <div className={styles.OrganizationBox}>
        <Row align="stretch" style={{ marginBottom: '30px' }}>
          <Typography.Title style={{ flex: 1 }} level={4}>
            {t('groups.groupsManagement')}
          </Typography.Title>
          <Row gutter={8}>
            <Col flex="auto">
              <Input
                style={{ height: '32px' }}
                prefix={<SearchOutlined />}
                placeholder={t('search')}
                value={keyword}
                onChange={e =>
                  dispatch(GroupActions.setKeyword(e.target.value))
                }
                onPressEnter={handleSearch}
                allowClear
              />
            </Col>
            <Col>
              <Tooltip title={t('groups.createGroup')}>
                <Button
                  icon={<PlusOutlined style={{ fontSize: '12px' }} />}
                  onClick={showCreateUpdateJobTitleModal}
                ></Button>
              </Tooltip>
            </Col>
            <Col>
              <Dropdown menu={{ items: downloadMenuItems }} trigger={['hover']}>
                <Button icon={<DownloadOutlined />}>{t('download')}</Button>
              </Dropdown>
            </Col>
            <Col>
              <Button icon={<UploadOutlined />} onClick={handleOpenUploadModal}>
                {t('upload')}
              </Button>
            </Col>
          </Row>
        </Row>
        <Spin spinning={loading} size="large">
          <div className={styles.ListContent}>
            <GroupsTable
              data={groupData}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDeleteMultiple={handleDeleteMultiple}
              selectedRowKeys={selectedRowKeys}
              onSelectionChange={setSelectedRowKeys}
              columnFilters={columnFilters}
              onTableChange={handleTableChange}
            />
          </div>
        </Spin>

        <div
          className={styles.Pagination}
          style={{
            width: `calc(100% - ${leftMenuCollapsed ? '146' : '266'}px)`,
          }}
        >
          <Pagination
            current={page}
            total={totalCount}
            pageSize={pageSize}
            showSizeChanger
            responsive
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
          />
        </div>
      </div>
    </WaitOverlay>
  )
}
